import { Divider } from '@ps-design-system/components/Divider';
import { ListingSummary } from '@ps-design-system/components/ListingSummary';
import { Text } from '@ps-design-system/components/Text';
import StarIcon from '@ps-design-system/icons/StarFilled/StarFilled16';
import PropTypes from 'prop-types';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';
import ProcessingPage from '../../app-bookings-request/ProcessingPage';
import Transactable from '../../shared-components/flow-transactable/transactable-main';
import SharedIntlProviderHOC from '../../shared-components/hocs/sharedIntlProviderHOC';
import { BookingDetailsForm } from '../checkout-forms/BookingDetailsForm/BookingDetailsForm';
import { PaymentForm } from '../checkout-forms/PaymentForm/PaymentForm';
import {
  getCheckoutRedirectUrl,
  getCurrencyFromListing
} from '../checkout-utils';
import { useCheckoutContext } from '../CheckoutContextProvider';
import { SplitPaymentPriceSummary } from '../price-summaries/SplitPaymentPriceSummary/SplitPaymentPriceSummary';
import { trackEvent } from '../services/analytics-service';
import { BillingAddressSection } from './BillingAddressSection';
import { BookingRequestErrorDialog } from './BookingRequestErrorDialog';
import { BookingRequestSummaryCard } from './BookingRequestSummaryCard';
import { CancellationPolicySection } from './CancellationPolicySection';
import { IdentityVerificationSection } from './IdentityVerificationSection';
import { RequestToBookSection } from './RequestToBookSection';
import { SocialEventPolicySection } from './SocialEventPolicySection';

export const BookingRequestPage = ({ intl, accessToken, profile }) => {
  const {
    quote,
    isFetchingQuote,
    listing,
    customerBillingAddress,
    IDVInfo,
    paymentProcessing,
    paymentForm,
    errorMsg,
    setErrorMsg
  } = useCheckoutContext();

  const { booking, risk } = quote?.spaceRentalContract?.meta || {};

  const urlPrefix = SharedIntlProviderHOC.getUrlPrefix()
    ? `/${SharedIntlProviderHOC.getUrlPrefix()}`
    : '';

  React.useEffect(() => {
    trackEvent('single page checkout viewed', {
      listing_id: listing?._id,
      sso_id: profile?.SSOid
    });

    trackEvent('viewed bkg request form', {
      listing_id: listing?._id,
      booking_id: booking?._id,
      sso_id: profile?.SSOid,
      is_single_page_checkout: true
    });
  }, []);

  const handleRequestSuccess = (response) => {
    window.location = getCheckoutRedirectUrl(
      intl.locale,
      response?.spaceRentalContract?.meta?.booking,
      listing
    );
  };

  const checkTransactability = (cb) => {
    if (!profile || !accessToken) {
      location.hash = 'a=signup&source=msg';
    } else {
      const action = () => {
        if (cb) {
          cb();
        }
      }
      ReactDOM.render(
        <SharedIntlProviderHOC>
          <Transactable user={profile} intendedAction={action} />
        </SharedIntlProviderHOC>,
        document.getElementById('modal-container')
      );
    }
  };

  if (paymentProcessing) {
    return <ProcessingPage />;
  }

  return (
    <div
      className="ps-container-lg-margin ps-container-fixed"
      style={{ paddingTop: '64px' }}
    >
      <div className="ps-row">
        <div className="col-xs-12 col-lg-7">
          <div>
            <Text
              typeface="headline-4-b"
              element="h1"
              style={{ margin: '0px' }}
            >
              <FormattedMessage
                id="booking_request_page.request_to_book"
                defaultMessage="Request to book"
              />
            </Text>
          </div>

          <div className="d-lg-none" style={{ marginTop: '32px' }}>
            <ListingSummary
              listing={listing}
              icons={
                <Text
                  typeface="body-1"
                  className="tw-text-gray-600"
                  prefix={<StarIcon className="tw-text-gray-900" />}
                  testId="listing-review-stars"
                >
                  <FormattedMessage
                    id="booking_request_page.review_stars"
                    defaultMessage="{stars} ({count}) Reviews"
                    values={{
                      stars: listing?.space_review_stars,
                      count: listing?.space_review_count
                    }}
                  />
                </Text>
              }
            />
          </div>

          <div style={{ margin: '32px 0 24px 0' }}>
            <Text
              typeface="headline-3-b"
              element="h2"
              style={{ margin: '0px' }}
            >
              <FormattedMessage
                id="booking_request_page.your_event"
                defaultMessage="Your event"
              />
            </Text>
          </div>

          <BookingDetailsForm intl={intl} />

          <PaymentForm
            accessToken={accessToken}
            SSOid={profile?.SSOid}
            intl={intl}
            urlPrefix={urlPrefix}
          />

          {!customerBillingAddress?.has_full_address && (
            <div style={{ marginTop: '24px' }}>
              <BillingAddressSection />
            </div>
          )}

          {risk?.guest_social_events_policy_agreement_required && (
            <>
              <Divider />
              <SocialEventPolicySection urlPrefix={urlPrefix} />
            </>
          )}

          {IDVInfo?.guest_needs_verification && (
            <>
              <Divider />
              <IdentityVerificationSection
                urlPrefix={urlPrefix}
                SSOid={profile?.SSOid}
                intl={intl}
                accessToken={accessToken}
              />
            </>
          )}

          {booking?.cancellation_policy && (
            <>
              <Divider style={{ marginBottom: '16px' }} />
              <CancellationPolicySection
                cancellationPolicy={booking.cancellation_policy}
              />
            </>
          )}

          <Divider
            style={
              booking?.cancellation_policy
                ? {
                    marginTop: '16px'
                  }
                : {}
            }
          />

          <RequestToBookSection
            urlPrefix={urlPrefix}
            handleRequestSuccess={handleRequestSuccess}
            checkTransactability={checkTransactability}
            bookingId={booking?._id}
            SSOid={profile?.SSOid}
          />
        </div>
        <div className="col-xs-12 col-lg-5 d-xs-none d-lg-block">
          <div style={{ position: 'sticky', top: '136px' }}>
            <BookingRequestSummaryCard
              listing={listing}
              booking={booking}
              isFetchingQuote={isFetchingQuote}
              urlPrefix={urlPrefix}
              SSOid={profile?.SSOid}
              accessToken={accessToken}
            />

            {paymentForm?.watch('selectedPaymentOption') === 'pay_partial' && (
              <div style={{ marginTop: '24px' }}>
                <SplitPaymentPriceSummary
                  splitPaymentCalculations={
                    quote?.masterContract?.calculations?.guest?.split_payment
                  }
                  splitPaymentChargeDate={
                    quote?.masterContract?.split_payment_charge_date
                  }
                  currency={getCurrencyFromListing(listing)}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <BookingRequestErrorDialog
        errorMsg={errorMsg}
        isOpen={!!errorMsg}
        setIsOpen={() => setErrorMsg('')}
      />
    </div>
  );
};

BookingRequestPage.displayName = 'BookingRequestPage';
BookingRequestPage.propTypes = {
  intl: PropTypes.object,
  profile: PropTypes.object,
  accessToken: PropTypes.string
};
